/**
 * SubscriptionManagePage — Single management surface for the user's subscription.
 *
 * Renders different states based on `useSubscriptionStatus().displayState`:
 *
 *   active           — status hero + cancel CTA + billing portal
 *   canceled_active  — amber banner ("ends on X" + Resume CTA) + benefit-loss list
 *   past_due         — red banner + update-card CTA + billing portal
 *   expired          — "Free" state + Pro-again offer card + history
 *   none             — fallback (rare — user reached this without a subscription)
 *
 * Reuses:
 *   - SubPageLayout            (back-arrow header, no top/bottom nav)
 *   - SubscriptionAlertBanner  (tonal banner)
 *   - BenefitDeltaList         (what changes when Pro ends)
 *   - PremiumBadge             (visual Pro mark)
 *   - usePremiumPrice / useSubscriptionStatus / useManageSubscription hooks
 */

import React, { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ChevronRight, CreditCard, FileText, Loader2, ShoppingBag, Star } from 'lucide-react';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { Button } from '@/components/ui/button';
import { PremiumBadge } from '@/components/ui/PremiumBadge';
import { useI18n } from '@/contexts/I18nContext';
import { useToast } from '@/shared/hooks/use-toast';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { usePremiumPrice } from '../hooks/usePremiumPrice';
import {
  useBillingPortal,
  useResumeSubscription,
} from '../hooks/useManageSubscription';
import { SubscriptionAlertBanner } from '../components/SubscriptionAlertBanner';
import { BenefitDeltaList, type BenefitDeltaItem } from '../components/BenefitDeltaList';

function formatDate(date: Date | null | undefined): string {
  if (!date) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Visible benefits the user loses when Pro ends. Sourced from premium_features. */
function useBenefitsAtRisk(): BenefitDeltaItem[] {
  const { t } = useI18n();
  // Mirrors the paywall benefit list (Phase 3) — kept in sync intentionally.
  return useMemo(
    () => [
      { featureKey: 'profile.premium_badge', label: t('premium.benefits.badge') },
      { featureKey: 'search.premium_boost',  label: t('premium.benefits.boost') },
      { featureKey: 'roll_review.create',    label: t('premium.benefits.roll_review') },
    ],
    [t],
  );
}

export default function SubscriptionManagePage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { toast } = useToast();
  const { subscription, displayState, isPro, isLoading } = useSubscriptionStatus();
  const { displayAmount } = usePremiumPrice();

  const billingPortal = useBillingPortal();
  const resume = useResumeSubscription();

  const benefitsAtRisk = useBenefitsAtRisk();
  const periodEnd = formatDate(subscription?.currentPeriodEnd ?? null);

  const onBillingPortal = () => {
    billingPortal.mutate(undefined, {
      onError: (e) =>
        toast({
          title: t('premium.manage.portal_error'),
          description: e instanceof Error ? e.message : undefined,
          variant: 'destructive',
        }),
    });
  };

  const onResume = () => {
    resume.mutate(undefined, {
      onSuccess: () => toast({ title: t('premium.manage.resume_success') }),
      onError: (e) =>
        toast({
          title: t('premium.manage.resume_error'),
          description: e instanceof Error ? e.message : undefined,
          variant: 'destructive',
        }),
    });
  };

  if (!isPro && !isLoading && displayState !== 'expired' && displayState !== 'suspended') {
    return <Navigate to="/subscribe" replace />;
  }

  // ── Expired state: fully different layout ─────────────────────────────────
  if (displayState === 'expired') {
    return (
      <SubPageLayout title={t('premium.manage.title')} hideBottomNav>
        <div className="max-w-md mx-auto pb-12 flex flex-col">
          {/* Centered hero */}
          <ExpiredHero periodEnd={periodEnd} />

          {/* "Pro de novo" reactivation row */}
          <ReactivateRow onClick={() => navigate('/subscription')} />

          {/* Billing section */}
          <SectionLabel className="px-5 pt-6 pb-3">
            {t('premium.manage.billing_section')}
          </SectionLabel>

          {subscription && (
            <ListRow
              icon={<FileText size={20} />}
              label={t('premium.billing.title')}
              onClick={() => navigate('/settings/subscription/billing')}
              border
            />
          )}

          <ListRow
            icon={<CreditCard size={20} />}
            label={t('premium.manage.payment_methods')}
            onClick={() => navigate('/settings/payment-methods')}
          />

          <ListRow
            icon={<ShoppingBag size={20} />}
            label={t('premium.manage.my_payments')}
            ariaLabel="My Payments"
            onClick={() => navigate('/my-purchases')}
          />
        </div>
      </SubPageLayout>
    );
  }

  // ── All other states ───────────────────────────────────────────────────────
  return (
    <SubPageLayout title={t('premium.manage.title')} hideBottomNav>
      <div className="max-w-md mx-auto pt-2 pb-12 flex flex-col gap-4">
        {/*
         * ── Pro status card group ──────────────────────────────────────
         * The status hero and the billing-history entry belong to the same
         * visual unit ("Suas cobranças está dentro do card Pro" — Ohana QA
         * round-2). They live inside the same <section> so the relationship
         * is asserted by ancestry rather than by DOM index.
         */}
        <section data-testid="pro-status-card" className="flex flex-col gap-4">
          <StatusHero
            isLoading={isLoading}
            displayState={displayState}
            isPro={isPro}
            displayAmount={displayAmount}
            periodEnd={periodEnd}
          />

        {/* ── State-specific banner + Resume CTA ──────────────────────── */}
        {displayState === 'canceled_active' && (
          <div data-testid="manage-banner-canceled">
            <SubscriptionAlertBanner
              tone="amber"
              title={t('premium.manage.banner_canceled_active_title', { date: periodEnd })}
              body={t('premium.manage.banner_canceled_active_body')}
              cta={
                <Button
                  data-testid="manage-resume-cta"
                  variant="primary"
                  onClick={onResume}
                  disabled={resume.isPending}
                  className="w-full"
                >
                  {resume.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    t('premium.manage.resume_subscription')
                  )}
                </Button>
              }
            />
          </div>
        )}

        {displayState === 'past_due' && (
          <div data-testid="manage-banner-past-due">
            <SubscriptionAlertBanner
              tone="red"
              title={t('premium.manage.banner_past_due_title')}
              body={t('premium.manage.banner_past_due_body')}
              cta={
                <Button data-testid="manage-update-card-cta" variant="primary" onClick={onBillingPortal} className="w-full">
                  {t('premium.manage.update_card')}
                </Button>
              }
            />
          </div>
        )}

        {displayState === 'suspended' && (
          <SubscriptionAlertBanner
            tone="red"
            title={t('premium.manage.banner_suspended_title')}
            body={t('premium.manage.banner_suspended_body')}
            cta={
              <Button variant="primary" onClick={onBillingPortal} className="w-full">
                {t('premium.manage.update_card')}
              </Button>
            }
          />
        )}

        {/* ── What changes when Pro ends — only for canceled_active ────── */}
        {displayState === 'canceled_active' && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <BenefitDeltaList
              mode="losing"
              title={t('premium.manage.what_changes_title')}
              items={benefitsAtRisk}
            />
            <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
              {t('premium.manage.resume_microcopy')}
            </p>
          </div>
        )}

          {/* ── Billing history (inline page, no Stripe portal) ─────────── */}
          {subscription && (
            <ActionRow
              icon={<FileText size={16} />}
              label={t('premium.billing.title')}
              sublabel={t('premium.manage.billing_portal_hint')}
              onClick={() => navigate('/settings/subscription/billing')}
            />
          )}
        </section>

        <ActionRow
          icon={<CreditCard size={16} />}
          label={t('premium.manage.payment_methods')}
          onClick={() => navigate('/settings/payment-methods')}
        />

        <ActionRow
          icon={<ShoppingBag size={16} />}
          label={t('premium.manage.my_payments')}
          ariaLabel="My Payments"
          onClick={() => navigate('/my-purchases')}
        />

        {/* ── Cancel CTA only when actively Pro (not already canceled) ─── */}
        {isPro && subscription && !subscription.cancelAtPeriodEnd && (
          <div data-testid="sticky-footer" className="pt-2 flex flex-col gap-2">
            <button
              type="button"
              data-testid="manage-cancel-entry"
              onClick={() => navigate('/settings/subscription/cancel')}
              className="w-full flex items-center justify-center h-[47px] rounded-full bg-[rgba(239,68,68,0.10)] border border-[rgba(239,68,68,0.30)] text-[#ef4444] text-[14px] font-medium hover:bg-[rgba(239,68,68,0.16)] transition-colors"
            >
              {t('premium.manage.cancel_entry')}
            </button>
            <p className="text-[11px] text-white/40 text-center leading-relaxed px-4">
              {t('premium.manage.cancel_microcopy')}
            </p>
          </div>
        )}

        {/* Suspended users need to fix their card, not re-subscribe */}
        {!isPro && displayState !== 'suspended' && (
          <Button variant="primary" onClick={() => navigate('/subscription')} className="w-full">
            {t('premium.cta.subscribe')}
          </Button>
        )}
      </div>
    </SubPageLayout>
  );
}

// ── Reusable inner pieces ────────────────────────────────────────────────────

/** Per-state status pill colour tokens. */
function useStatusPill(displayState: string, isLoading: boolean) {
  const { t } = useI18n();
  if (isLoading) {
    return { label: '…', dot: 'bg-white/30', pill: 'bg-white/5 border-white/10 text-white/40' };
  }
  switch (displayState) {
    case 'active':
      return {
        label: t('premium.manage.status_active'),
        dot:   'bg-[#22c55e]',
        pill:  'bg-[rgba(34,197,94,0.14)] border-[rgba(34,197,94,0.3)] text-[#22c55e]',
      };
    case 'past_due':
      return {
        label: t('premium.manage.status_past_due'),
        dot:   'bg-amber-400',
        pill:  'bg-amber-400/10 border-amber-400/30 text-amber-400',
      };
    case 'canceled_active':
      return {
        label: t('premium.manage.status_canceled_active'),
        dot:   'bg-amber-400',
        pill:  'bg-amber-400/10 border-amber-400/30 text-amber-400',
      };
    case 'suspended':
      return {
        label: t('premium.manage.status_suspended'),
        dot:   'bg-red-500',
        pill:  'bg-red-500/10 border-red-500/30 text-red-400',
      };
    case 'expired':
      return {
        label: t('premium.manage.status_expired'),
        dot:   'bg-white/30',
        pill:  'bg-white/5 border-white/10 text-white/50',
      };
    default:
      return {
        label: t('premium.manage.status_none'),
        dot:   'bg-white/30',
        pill:  'bg-white/5 border-white/10 text-white/50',
      };
  }
}

function StatusHero({
  isLoading,
  displayState,
  isPro,
  displayAmount,
  periodEnd,
}: {
  isLoading: boolean;
  displayState: string;
  isPro: boolean;
  displayAmount: string | null;
  periodEnd: string;
}) {
  const { t } = useI18n();
  const statusPill = useStatusPill(displayState, isLoading);

  const periodHint = (() => {
    if (!periodEnd) return null;
    if (displayState === 'canceled_active') return t('premium.manage.ends_on', { date: periodEnd });
    if (isPro) return t('premium.manage.renewal', { date: periodEnd });
    return null;
  })();

  return (
    <div className="rounded-xl overflow-hidden border border-white/[0.06] bg-gradient-to-b from-[rgba(215,46,22,0.10)] to-[rgba(215,46,22,0)] px-5 pt-5 pb-4 space-y-3">
      {/* Row 1: PRO badge + plan interval (left) | Status pill (right) */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {isPro && <PremiumBadge size="sm" />}
          <span className="text-sm font-semibold text-white">
            {t('premium.default_interval_label')}
          </span>
        </div>
        <span
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${statusPill.pill}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${statusPill.dot}`} />
          {statusPill.label}
        </span>
      </div>

      {/* Row 2: Price */}
      <p className="leading-none">
        {isLoading ? (
          <span className="text-[32px] font-extrabold text-white/30">…</span>
        ) : displayAmount ? (
          <>
            <span className="text-[32px] font-extrabold tracking-tight text-white">
              {displayAmount}
            </span>
            <span className="ml-0.5 text-[13px] text-white/50">
              /{t('premium.default_interval')}
            </span>
          </>
        ) : (
          <span className="text-[32px] font-extrabold text-white/30">—</span>
        )}
      </p>

      {/* Row 3: Renewal / expiry hint */}
      {periodHint && (
        <p className="text-[13px] text-white/70 leading-snug">{periodHint}</p>
      )}
    </div>
  );
}

function ActionRow({
  icon,
  label,
  sublabel,
  onClick,
  disabled,
  ariaLabel,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="w-full text-left rounded-xl border border-white/10 bg-white/5 p-3 hover:bg-white/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/80">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white">{label}</p>
          {sublabel && <p className="text-[11px] text-white/50 leading-snug mt-0.5">{sublabel}</p>}
        </div>
      </div>
    </button>
  );
}

// ── Expired-state components ─────────────────────────────────────────────────

/** Section label used in the expired layout. */
function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-[11px] font-bold tracking-[2.5px] text-white/50 uppercase ${className ?? ''}`}>
      {children}
    </p>
  );
}

/** Centered "Free / plano atual" hero shown when subscription is expired. */
function ExpiredHero({ periodEnd }: { periodEnd: string }) {
  const { t } = useI18n();
  return (
    <div className="border-b border-white/[0.06] px-5 py-6 text-center flex flex-col items-center gap-3">
      <p className="text-[11px] font-bold tracking-[1.5px] text-white/50 uppercase">
        {t('premium.manage.expired_plan_label')}
      </p>
      <p className="text-[24px] font-bold text-white leading-none">
        {t('premium.manage.expired_plan_name')}
      </p>
      <p className="text-[13px] text-white/50 leading-relaxed max-w-[300px]">
        {t('premium.manage.expired_body', { date: periodEnd })}
      </p>
    </div>
  );
}

/** Full-width list row with border-bottom — used in the expired billing section. */
function ListRow({
  icon,
  label,
  sublabel,
  onClick,
  disabled,
  border = false,
  ariaLabel,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  onClick: () => void;
  disabled?: boolean;
  border?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`w-full text-left flex items-center gap-4 px-5 py-[15px] hover:bg-white/[0.03] transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${border ? 'border-b border-white/[0.06]' : ''}`}
    >
      <span className="shrink-0 text-white/50">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium text-white">{label}</p>
        {sublabel && (
          <p className="text-[12px] text-white/50 mt-0.5">{sublabel}</p>
        )}
      </div>
      <ChevronRight size={16} className="shrink-0 text-white/30" />
    </button>
  );
}

/** "Pro de novo" reactivation row — Figma-style list row with inline PRO badge. */
function ReactivateRow({ onClick }: { onClick: () => void }) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      data-testid="manage-reactivate-row"
      onClick={onClick}
      className="w-full text-left flex items-center gap-4 px-5 py-[15px] bg-[rgba(215,46,22,0.04)] border-b border-white/[0.06] hover:bg-[rgba(215,46,22,0.08)] transition-colors"
    >
      <Star size={20} className="shrink-0 text-[#D72E16]" fill="currentColor" strokeWidth={0} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[14px] font-medium text-white">
            {t('premium.manage.reactivate_title')}
          </span>
          <PremiumBadge size="xs" />
        </div>
        <p className="text-[12px] text-white/50 mt-0.5">
          {t('premium.manage.reactivate_sublabel')}
        </p>
      </div>
      <ChevronRight size={16} className="shrink-0 text-white/30" />
    </button>
  );
}
